import { useState, useCallback, useEffect } from 'react';
import { FollowService } from '@/services/followService';
import { FollowRequestService, FollowRequestStatus } from '@/services/followRequestService';
import { toast } from 'sonner';

interface UseFollowSystemProps {
  targetUserId: string;
  targetUsername: string;
  isPrivateProfile: boolean;
  onSuccess?: () => void;
}

export const useFollowSystem = ({ 
  targetUserId, 
  targetUsername, 
  isPrivateProfile,
  onSuccess 
}: UseFollowSystemProps) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followRequestStatus, setFollowRequestStatus] = useState<FollowRequestStatus>('none');
  const [loading, setLoading] = useState(false);

  /**
   * Initialize follow status
   */
  const initializeStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [isFollowingUser, requestStatus] = await Promise.all([
        FollowService.checkIsFollowing(targetUserId),
        FollowRequestService.getFollowRequestStatus(targetUserId)
      ]);

      setIsFollowing(isFollowingUser);
      setFollowRequestStatus(requestStatus);
    } catch (error) {
      console.error('Error initializing follow status:', error);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  /**
   * Handle follow action based on profile type and current status
   */
  const handleFollowAction = useCallback(async () => {
    setLoading(true);
    
    try {
      let result;

      if (isFollowing) {
        // Unfollow user
        result = await FollowService.unfollowUser(targetUserId);
        
        if (result.success) {
          setIsFollowing(false);
          setFollowRequestStatus('none');
          toast.success(`Non segui più @${targetUsername}`);
          
          // Clean up any pending requests when unfollowing
          await FollowRequestService.cancelFollowRequest(targetUserId);
        }
      } else if (followRequestStatus === 'pending') {
        // Cancel pending request
        result = await FollowRequestService.cancelFollowRequest(targetUserId);
        
        if (result.success) {
          setFollowRequestStatus('none');
          toast.success('Richiesta di follow cancellata');
        }
      } else {
        // Send new follow or request
        if (isPrivateProfile) {
          result = await FollowRequestService.sendFollowRequest(targetUserId);
          
          if (result.success) {
            setFollowRequestStatus('pending');
            toast.success(`Richiesta di follow inviata a @${targetUsername}`);
          }
        } else {
          result = await FollowService.followUser(targetUserId);
          
          if (result.success) {
            setIsFollowing(true);
            setFollowRequestStatus('accepted');
            toast.success(`Ora segui @${targetUsername}!`);
          }
        }
      }

      if (!result.success) {
        console.error('Follow action failed:', result.error);
        
        // Handle specific error types
        if (result.error?.message?.includes('not authenticated')) {
          toast.error('Devi essere autenticato per seguire gli utenti');
        } else if (result.error?.message?.includes('permission denied')) {
          toast.error('Errore di permessi. Prova a rifare il login.');
        } else {
          toast.error('Errore nell\'operazione. Riprova.');
        }
      } else {
        // Call success callback for parent component updates
        onSuccess?.();
      }
    } catch (error) {
      console.error('Unexpected error in follow action:', error);
      toast.error('Errore imprevisto. Riprova.');
    } finally {
      setLoading(false);
    }
  }, [isFollowing, followRequestStatus, targetUserId, targetUsername, isPrivateProfile, onSuccess]);

  /**
   * Get button text and state
   */
  const getButtonState = useCallback(() => {
    if (loading) {
      return {
        text: 'Caricamento...',
        disabled: true,
        variant: 'default' as const
      };
    }

    if (isFollowing) {
      return {
        text: 'Non seguire',
        disabled: false,
        variant: 'secondary' as const
      };
    }

    switch (followRequestStatus) {
      case 'pending':
        return {
          text: 'Richiesta inviata',
          disabled: false,
          variant: 'outline' as const
        };
      case 'rejected':
        return {
          text: 'Richiesta rifiutata',
          disabled: true,
          variant: 'destructive' as const
        };
      default:
        if (isPrivateProfile) {
          return {
            text: 'Richiedi di seguire',
            disabled: false,
            variant: 'default' as const
          };
        } else {
          return {
            text: 'Segui',
            disabled: false,
            variant: 'default' as const
          };
        }
    }
  }, [loading, isFollowing, followRequestStatus, isPrivateProfile]);

  return {
    isFollowing,
    followRequestStatus,
    loading,
    handleFollowAction,
    initializeStatus,
    getButtonState
  };
};